import { IMessageModel, MessageSeverity } from "~app/common/components/message-panel/message-panel.models";
import { Anskaffningsformer, Arbetsordertyper } from "~app/common/models/constants";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { IAtgard } from "~app/features/planera/model/IAtgard";
import { IOperationBase } from "~app/features/planera/model/IOperationBase";
import { ArbetsorderViol3ButtonComponent } from "~app/features/shared/components/arbetsorder-viol3-button/arbetsorder-viol3-button.component";
import { IArbetsorderVirkesorderRule } from "~app/features/virkesorder/virkesorder.models";
import { VirkesorderService } from "~app/features/virkesorder/virkesorder.service";
import { IArbetsorderValidationInfo } from "../../../shared/components/arbetsorder-viol3-button/IArbetsorderValidationInfo";
import { ITplHandelserArbetsorder } from "./dtos";

export class TplHandelseViewModel implements IArbetsorderValidationInfo {

    selected = false;
    canSendToViol: () => boolean;
    periodsattPeriod?: string;
    operationer?: IOperationBase[];
    atgarder: IAtgard[];
    viol3VirkeskopPilotAnvandare: boolean;

    constructor(
        private model: ITplHandelserArbetsorder,
        private readonly virkesorderService: VirkesorderService) {
        this.init();
    }

    updateModel(model: ITplHandelserArbetsorder) {
        this.model = model;
        this.init();
    }

    get id(): number {
        return this.model.arbetsorderId;
    }

    get produktsatt(): boolean {
        return this.model.produktsatt;
    }

    get eudrValid(): boolean {
        return this.model.eudrValid;
    }

    get koordinaterVerifierade(): boolean {
        return this.model.koordinaterVerifierade;
    }

    get arbetsorderBeteckning() {
        return this.model.arbetsorderBeteckning;
    }

    get arbetsorderNamn() {
        return this.model.arbetsorderNamn;
    }

    get arbetsorderId() {
        return this.model.arbetsorderId;
    }

    get isDirty(): boolean {
        return this.model.isDirty;
    }

    get voStatus() {
        return this.model.voStatus;
    }

    get hasTransmissionErrorViol3(): boolean {
        return this.model.hasTransmissionErrorViol3;
    }

    get dirtyProduktion(): boolean {
        return this.model.dirtyProduktion;
    }

    get dirtyTransport(): boolean {
        return this.model.dirtyTransport;
    }

    get skickad() {
        return this.model.skickad;
    }

    get pending() {
        return this.model.pending;
    }

    set pending(val: boolean) {
        this.model.pending = val;
    }

    get antalAktivaVirkesorderrader() {
        return this.model.antalAktivaVirkesorderrader;
    }

    get isSelected() {
        return this.selected;
    }

    set isSelected(val: boolean) {
        this.selected = val;
    }

    get viol3(): boolean {
        return this.model.viol3;
    }

    get anskaffningsform(): string {
        return this.model.anskaffningsform;
    }

    get isExternArbetsorder(): boolean {
        return ((this.anskaffningsform === Anskaffningsformer.EgenTerminal && !this.viol3) ||
            (this.anskaffningsform === Anskaffningsformer.Import && !this.viol3) ||
            this.anskaffningsform === Anskaffningsformer.CentralaKop) &&
            this.model.arbetsorderTyp === Arbetsordertyper.IngenProduktion;
    }

    get isTerminalImport(): boolean {
        return (this.anskaffningsform === Anskaffningsformer.EgenTerminal ||
            this.anskaffningsform === Anskaffningsformer.Import) &&
            this.model.arbetsorderTyp === Arbetsordertyper.IngenProduktion;
    }

    get alerts(): IMessageModel[] {
        if (!this.hasWarningsOrErors) {
            return undefined;
        }

        return [
            {
                anchor: undefined,
                metadata: undefined,
                message: "Fel/varningar måste hanteras i produktionsplanera",
                severity: MessageSeverity.Warning,
                code: undefined,
                validationScope: undefined
            }
        ];
    }

    get hasWarningsOrErors(): boolean {
        return this.model.hasWarnings || this.model.hasErrors;
    }

    get overrideValidationRules(): IArbetsorderVirkesorderRule[] {
        return [
            {
                overrideFunctionName: ArbetsorderViol3ButtonComponent.isAtgarderIsTillgangligaMethodname,
                text: "Alla åtgärder är tillgängliga eller högre",
                validationFunction: this.isAtgarderIsTillgangliga
            },
            {
                overrideFunctionName: ArbetsorderViol3ButtonComponent.isSchemalagdMethodname,
                text: "Arbetsordern är schemalagd",
                validationFunction: this.isSchemalagd
            }
        ];
    }
    isAtgarderIsTillgangliga = () => this.model.atgarderIsTillgangliga;
    isSchemalagd = () => this.model.isSchemalagd;

    private init(): void {
        this.canSendToViol = this.virkesorderService.createCanSendVirkesorderFunc({
            rules: {
                areKoordinaterVerifierade: () => this.model.koordinaterVerifierade,
                isProduktsatt: () => this.model.produktsatt,
                isVoContractDirty: () => this.isDirty,
                isSchemalagd: () => this.model.isSchemalagd,
                isViol2Disabled: () => !this.viol3,
                hasActiveValsVo: () => false, // hårdkodat då tjänsten bara läser från Virksorder-tabellen?!
                hasHandelssortiment: () => this.model.hasHandelssortiment,
                hasValidationMessages: () => this.hasWarningsOrErors,
                isArbetsorderDirty: () => false,
                atgarderIsTillgangliga: () => this.model.atgarderIsTillgangliga,
                isPending: () => this.pending,
                hasVirkesorderradsBegransning: () => {
                    const isSent = () => !ObjectUtils.isNullOrUndefined(this.model.senastAckadGeneration);
                    if (!isSent() && this.model.antalAktivaVirkesorderrader > 50) {
                        return true;
                    }

                    if (isSent() && this.model.antalAktivaVirkesorderrader > 60) {
                        return true;
                    }
                    return false;
                }
            },
            isViol2: !this.model.viol3
        });
    }
}
